import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { isEmpty, map, replace } from "lodash"
import classnames from "classnames"
import { CardContent, Paper } from "@mui/material"
import { DATE } from "helpers/shapes"
import { numberFormatter, smallNumberFormatter } from "helpers/formatters/numbers"
import { AGGREGATION_SHAPE } from "helpers/factorClassification"
import { FACTOR_TAB_OPTION_SHAPE } from "helpers/compositeSecurities"
import { computeCSVData } from "helpers/csv"
import { useTrack } from "helpers/analytics"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import AggregationDropdown, { OPTION_SHAPE } from "components/list-item-dropdown"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import ExcelDownload from "components/excel-download"
import AnalyzeFactorSensitivityDetails from "modules/analyze-composite-factor-sensitivity-details"
import { THRESHOLD_PARAMS } from "helpers/threshold"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import NotEnoughHistoricalFactorSensitivityData from "components/not-enough-historical-factor-sensitivity-data"

import "./style.scss"

export default function AnalyzeCompositeFactorSensitivity({
  account,
  modelId,
  analyzeViewSelectList,
  data,
  factorSensitivity,
  items,
  segment,
  positionSetId,
  positionSetType,
  positionSetName,
  classifications,
  filtersVisible,
  startDate,
  selectedDate,
  endDate,
  earliestDate,
  latestDate,
  setDate,
  openDialog,
  options,
  selectedOption,
  factorCategoryId,
  factorCategoryName,
  allCategoryItems,
  viewType,
  currentGrouping,
  expand,
  displayAddView,
  selectedViews,
  addSelectedView,
  removeSelectedView,
  selectFactorCategory,
  availableSelectViewFactors,
  selectedViewsFactors,
  aggregation,
  mustAggregateFactors,
  factorsClassification,
  aggregationOptions,
  displayAggregationToggle,
  setAggregationFunction,
  focusedChartLine,
  focusChartLine,
  blurChartLine,
  only,
  fileName,
  showNotHeld,
  base,
  baseSignature,
  setGrouping,
  toggle,
  toggleOnly,
  factorSensitivityType,
  thresholdParams,
  isOpPro,
  isActive,
}) {
  const makeTrackedAction = useCallback(fn => (...args) => { fn(...args, factorSensitivityType) }, [factorSensitivityType])

  const trackExcelDownload = useTrack({
    type: "composite-analyze-factor-sensitivity-graph-timeseries-download",
  })

  const navigate = useNavigate()

  function predictedFSRedirect() {
    const { pathname, search } = window.location
    navigate(`${replace(pathname, "historical", "predicted")}${search}`)
  }

  let content

  if (!earliestDate || !latestDate) {
    content = <NoData title="Factor Sensitivities" className="graph" />
  } else if (isEmpty(data) && factorSensitivityType === FACTOR_SENSITIVITY_TYPES.HISTORICAL) {
    content = <NotEnoughHistoricalFactorSensitivityData {...{ assetTypeName: positionSetType, isOpPro, startDate, handleSelectPredicted: predictedFSRedirect }} />
  } else {
    let rightLegendItems
    if (displayAggregationToggle) {
      rightLegendItems = [<AggregationDropdown key="aggregation-dropdown" setValue={setAggregationFunction} value={aggregation} listSubheader="Choose display type for style factors" label="Display Type" options={aggregationOptions} classes={{ toggle: "aggregation-toggle" }} />]
    }
    content = <CardContent className={classnames("card-content", { aggregation: displayAggregationToggle })}>
      <Paper className="content">
        <div className="graph-container">
          <div className="graph-top">
            {analyzeViewSelectList}
            <GraphTopBar {...{
              openDialog,
              displayAddView,
              addView: ({ id }) => addSelectedView(id),
              factors: availableSelectViewFactors,
              value: factorCategoryId,
              onTabChanged: (_, factorId) => selectFactorCategory(factorId),
              tabs: map(options, ({ id, label, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: (factorId) => removeSelectedView(factorId, options),
                label,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackExcelDownload} />],
            }}
            />
          </div>
          <div className="graph">
            <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} />}
              {...{
                // Number is already multiplied by 100 so can't use the smallPercentageFormatter
                tickFormat: (number) => `${smallNumberFormatter(number)}%`,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle: makeTrackedAction(toggle),
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: (factorId) => makeTrackedAction(toggleOnly(factorId, only !== factorId, items)),
                thresholdParams,
                rightLegendItems,
              }}
            />
          </div>
        </div>
        <div className="contributors">
          <AnalyzeFactorSensitivityDetails
            {...{
              account,
              modelId,
              positionSetId,
              positionSetType,
              positionSetName,
              factorSensitivity,
              segment,
              aggregation,
              mustAggregateFactors,
              factorsClassification,
              classifications,
              selectedDate,
              viewType,
              selectedViewsFactors,
              expand,
              currentGrouping,
              setGrouping,
              displayType: factorCategoryId,
              factorCategoryName,
              allCategoryItems,
              startDate,
              endDate,
              selectedOption,
              selectedViews,
              filtersVisible,
              showNotHeld,
              base,
              baseSignature,
              thresholdParams,
              factorSensitivityType,
              isActive,
            }}
          />
        </div>
      </Paper>
    </CardContent>
  }

  return (
    <div className="modules_analyze-composite-factor-sensitivity">
      {content}
    </div>
  )
}

AnalyzeCompositeFactorSensitivity.propTypes = {
  analyzeViewSelectList: PropTypes.element,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  positionSetId: PropTypes.string.isRequired,
  positionSetType: PropTypes.string.isRequired,
  positionSetName: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  factorSensitivity: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  currentGrouping: PropTypes.object,
  setGrouping: PropTypes.func.isRequired,
  classifications: PropTypes.array,
  categories: PropTypes.array,
  allCategoryItems: PropTypes.array.isRequired,
  expand: PropTypes.bool,
  startDate: DATE.isRequired,
  firstDate: DATE,
  selectedDate: DATE,
  endDate: DATE,
  earliestDate: DATE,
  latestDate: DATE,
  setDate: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  displayAggregationToggle: PropTypes.bool,
  mustAggregateFactors: PropTypes.bool,
  factorsClassification: PropTypes.object,
  favoriteFactors: PropTypes.object,
  aggregatedFactorsMetadata: PropTypes.object,
  displayAddView: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(FACTOR_TAB_OPTION_SHAPE).isRequired,
  selectedOption: PropTypes.object,
  factorCategoryId: PropTypes.string,
  factorCategoryName: PropTypes.string,
  viewType: PropTypes.string.isRequired,
  selectedViews: PropTypes.array,
  addSelectedView: PropTypes.func.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  selectFactorCategory: PropTypes.func.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  selectedViewsFactors: PropTypes.array,
  only: PropTypes.string,
  aggregation: AGGREGATION_SHAPE,
  setAggregationFunction: PropTypes.func.isRequired,
  aggregationOptions: PropTypes.arrayOf(OPTION_SHAPE),
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  segment: PropTypes.object,
  showNotHeld: PropTypes.bool.isRequired,
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  toggle: PropTypes.func.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  factorSensitivityType: PropTypes.string.isRequired,
  thresholdParams: THRESHOLD_PARAMS,
  isOpPro: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
}
